import _ from 'lodash'
import { withCompInfo } from '@wix/editor-elements-integrations'
import { color as colorUtils } from '@wix/thunderbolt-commons'
import {
	getMobileScaledFont,
	variantSeparator,
	getHeightPixelValue,
	getRotationInDegrees,
	getWidthPixelValue,
} from '@wix/thunderbolt-becky-root'
import { CompInfo } from '@wix/editor-elements-types/thunderbolt'
import type { Spx } from '@wix/thunderbolt-becky-types'
import type { WRichTextCSSVars } from './WRichText'
import { WRichTextSelectorCSSVars } from './WRichText'

const { getFromColorMap, extractThemeColor, isThemeColor } = colorUtils

const RESPONSIVE_TAGS = ':is(p,h1,h2,h3,h4,h5,h6,ul,ol,span[data-attr-richtext-marker],blockquote)' as const
const INTERACTION_TAGS = ':is(p,h1,h2,h3,h4,h5,h6,ul,ol,span,blockquote)' as const

export const css = withCompInfo<WRichTextCSSVars, any>()(
	[
		'compProps',
		'styleProperties',
		'compLayout',
		'compSingleLayout',
		'isMobileView',
		'hasResponsiveLayout',
		'experiments',
		'siteFonts',
		'siteFontsSpxResolved',
		// @ts-ignore - Remove once isOneDocMigrated is added to the compInfo
		'isOneDocMigrated',
	],
	({
		compProps,
		styleProperties,
		compLayout,
		compSingleLayout,
		isMobileView,
		hasResponsiveLayout,
		experiments,
		siteFonts,
		siteFontsSpxResolved,
		// @ts-ignore - Remove once isOneDocMigrated is added to the compInfo
		isOneDocMigrated,
	}) => {
		const { packed, verticalText, minHeight, minWidth } = compProps
		const scale = compProps.fontScale ?? isOneDocMigrated ? 1 : compLayout.scale
		// TODO: When moving this code to editor-elements this logic should be done using previewMapper
		const isMobilePreview = isMobileView && process.env.PACKAGE_NAME === 'thunderbolt-ds'
		const isPacked = packed || isMobilePreview

		const width = getWidthPixelValue(compLayout, compSingleLayout)
		const height = getHeightPixelValue(compLayout, compSingleLayout)
		const rotationInDegrees = getRotationInDegrees(compLayout, compSingleLayout)

		const isSupportSpOn = experiments['specs.thunderbolt.supportSpxInEEMappers']
		// Use resolved spx font map whenever applicable
		const fontMap = isSupportSpOn ? siteFontsSpxResolved : siteFonts

		/*
		 ** TODO: Parse html from compData and get only used fonts/colors
		 */
		const scaledFonts =
			scale !== 1
				? _.chain(fontMap)
						.mapValues((font: string, _key: string) => {
							return isMobileView ? getMobileScaledFont(font, scale!) : font
						})
						.mapKeys((_value, key) => `--${key}`)
						.value()
				: {}

		const resolveMinSize = (minSizeKey: number, sizeKey: number) =>
			isPacked ? minSizeKey : isMobileView ? sizeKey : minSizeKey || sizeKey

		/**
	   The minSize value is calculated by the following conditions (the example is for height, but same for width):
			if packed & mobile => 'min-height': 0
			if packed & !mobile => 'min-height': minHeight
			if !packed & mobile => 'min-height': height
			if !packed & !mobile => 'min-height': minHeight || height
	 */
		const minSizeCssValue =
			!rotationInDegrees &&
			!hasResponsiveLayout &&
			!(isPacked && isMobileView) &&
			resolveMinSize(verticalText ? minWidth : minHeight, (verticalText ? width : height)!)

		const minSizeCss = !minSizeCssValue
			? {}
			: verticalText
			? { 'min-width': `${minSizeCssValue}px` }
			: { '--min-height': `${minSizeCssValue}px` }

		const sizeCss =
			!hasResponsiveLayout &&
			(verticalText
				? // TODO: remove this once catharsis compMeasures experiment is merged, this logic will be handled with https://github.com/wix-private/thunderbolt/pull/27160
				  !(styleProperties.overflowX === 'hidden') && { width: 'auto' }
				: !(styleProperties.overflowY === 'hidden') && { height: 'auto' })

		const textDirection = styleProperties.direction ? { '--text-direction': styleProperties.direction } : {}

		return {
			...scaledFonts,
			...minSizeCss,
			...sizeCss,
			...textDirection,
			...(styleProperties.writingMode === 'vertical-rl' && { 'writing-mode': styleProperties.writingMode }),
			...(styleProperties.textOrientation === 'sideways' && {
				'text-orientation': styleProperties.textOrientation,
			}),
		}
	}
)

const getFontClass = (className: string) => {
	const findFontClass = /(font_\d+)/
	const fontClass = findFontClass.exec(className)
	return fontClass?.[1] || ''
}

const resolveColor = (color: string, siteColors: CompInfo['siteColors']) => {
	const parsedColor = extractThemeColor(color || 'none')
	return isThemeColor(parsedColor) ? getFromColorMap(color, siteColors) : color
}

const getFontSize = (
	experiments: CompInfo['experiments'],
	compData: CompInfo['compData'],
	styleProperties: CompInfo['styleProperties'],
	formatCssValue: CompInfo['formatCssValue'],
	siteFonts: CompInfo['siteFonts']
) => {
	const isSupportSpOn = experiments['specs.thunderbolt.supportSpxInEEMappers']

	const { text } = compData
	const { spx, fontSize: fontSizeOverride, minFontSize, maxFontSize } = styleProperties

	const textFontClass = getFontClass(text)
	const shouldFormatFontSizeOverride = fontSizeOverride && spx && isSupportSpOn
	const formattedFontSizeOverride = shouldFormatFontSizeOverride
		? formatCssValue(fontSizeOverride, (spx as unknown) as Spx, true)
		: fontSizeOverride
	const fontSize = fontSizeOverride ? formattedFontSizeOverride : siteFonts[textFontClass]

	const fontSizeWithMinOnly = `max(${minFontSize}, ${fontSize})`
	const fontSizeWithMaxOnly = `min(${maxFontSize}, ${fontSize})`
	const fontSizeWithMinAndMax = `max(${minFontSize}, min(${maxFontSize}, ${fontSize}))`

	const fallbackValue = fontSizeOverride ? { value: formattedFontSizeOverride } : {}
	const fontSizeWithoutMin = maxFontSize ? fontSizeWithMaxOnly : fallbackValue.value
	const minMaxFontSize =
		minFontSize && maxFontSize ? fontSizeWithMinAndMax : minFontSize ? fontSizeWithMinOnly : fontSizeWithoutMin

	// min/max font-size doesn't make sense if font-size is defined in absolute length(for example px)
	return fontSize && fontSize.endsWith('px') ? fallbackValue.value : minMaxFontSize
}

export const selectorToCss = withCompInfo<WRichTextSelectorCSSVars, any>()(
	[
		'compId',
		'compProps',
		'hasResponsiveLayout',
		'experiments',
		'styleProperties',
		'compData',
		'formatCssValue',
		// @ts-ignore - Remove once siteFonts is added to the compInfo
		'siteFonts',
		'siteColors',
		// @ts-ignore - Remove once isResponsive is added to the compInfo
		'isResponsive',
		// @ts-ignore - Remove once isDefaultVariant is added to the compInfo
		'isDefaultVariant',
	],
	({
		compId,
		compProps,
		hasResponsiveLayout,
		experiments,
		styleProperties,
		compData,
		formatCssValue,
		// @ts-ignore - Remove once siteFonts is added to the compInfo
		siteFonts,
		siteColors,
		// @ts-ignore - Remove once isResponsive is added to the compInfo
		isResponsive,
		// @ts-ignore - Remove once isDefaultVariant is added to the compInfo
		isDefaultVariant,
	}) => {
		const isResponsiveTagsOn = experiments['specs.thunderbolt.WRichTextResponsiveTagsInBlocksWidget']
		const isCompMappersRunsInCatharsis = experiments['specs.thunderbolt.compCssMappers_catharsis']

		const {
			color,
			backgroundColor,
			textScale__maxFontSize,
			textScale__minFontSize,
			lineHeight,
			fontFamily,
			fontWeight,
			fontStyle,
			textDecoration,
			textAlign,
			letterSpacing,
		} = styleProperties

		const scaleFontSize = `calc(${textScale__minFontSize}px + (${textScale__maxFontSize} - ${textScale__minFontSize}) * ((100vw - var(--minViewportSize) * 1px) / (var(--maxViewportSize) - var(--minViewportSize))))`
		const fontSize = getFontSize(experiments, compData, styleProperties, formatCssValue, siteFonts)

		const hasTextScale = textScale__maxFontSize && textScale__minFontSize
		const fontSizeResult = hasTextScale ? scaleFontSize : fontSize

		/**
		 * isResponsiveLayoutInClassicSiteAndExperimentIsOn will be true in case of a WRichText component inside a Blocks widget.
		 * This Code is a temp workaround for https://jira.wixpress.com/browse/EE-36819
		 * Wrapped with an experiment in case this would unexpectedly break backwards compatibility
		 */
		const isResponsiveLayoutInClassicSite = hasResponsiveLayout && !isResponsive
		const isResponsiveLayoutInClassicSiteAndExperimentIsOn = isResponsiveTagsOn && isResponsiveLayoutInClassicSite

		const isNotDefaultStyle = isCompMappersRunsInCatharsis
			? !isDefaultVariant
			: compId.split(variantSeparator).length === 2 // // compId can be "compId" or "compId___variant-separator___variantId"
		const responsiveLineHeight =
			_.isString(lineHeight) && lineHeight.endsWith('em')
				? lineHeight.substring(0, lineHeight.length - 2)
				: lineHeight

		const responsiveStyles = _({
			color: resolveColor(color, siteColors),
			'background-color': resolveColor(backgroundColor, siteColors),
			'font-family': fontFamily,
			'font-weight': fontWeight,
			'font-size': fontSizeResult,
			'font-style': fontStyle,
			'text-decoration': textDecoration,
			'text-align': textAlign,
			'letter-spacing': letterSpacing,
			'line-height': responsiveLineHeight,
			'min-height': hasTextScale ? '0vw' : '', // fixes safari not recalculating the calc bug in text scale
		})
			.pickBy((style) => style)
			.mapValues((style) =>
				isNotDefaultStyle || isResponsiveLayoutInClassicSite ? `${style} !important` : style
			) // We need to add !important because we need to override inline style
			.value()

		const result: Record<string, object> = {}

		if (hasResponsiveLayout && !isResponsiveLayoutInClassicSiteAndExperimentIsOn) {
			result[RESPONSIVE_TAGS] = responsiveStyles
		} else if (isNotDefaultStyle) {
			result[INTERACTION_TAGS] = responsiveStyles
		}

		const { overrideAlignment } = compProps
		if (overrideAlignment) {
			result['*'] = { 'text-align': `${overrideAlignment} !important` }
		}

		if (isResponsiveLayoutInClassicSiteAndExperimentIsOn) {
			result[''] = responsiveStyles
		}

		return result
	}
)
